import type {
  CashMovement,
  Expense,
  ExpenseCategory,
  ExpenseMethod,
  ExpenseProvider,
  ExpenseStatus,
} from '@horevia/contracts';
import type { BadgeProps } from '@horevia/ui';

type Tone = NonNullable<BadgeProps['tone']>;

/* -------------------------------------------------------------------------- */
/* Catalogues (contrats `expenses.ts`, D-052 / A-060)                           */
/* -------------------------------------------------------------------------- */

/** Ordre d'affichage : espèces d'abord (le cas le plus fréquent à la réception). */
export const EXPENSE_METHODS: ExpenseMethod[] = [
  'CASH',
  'MOBILE_MONEY',
  'CHEQUE',
  'BANK_TRANSFER',
  'BANK_WITHDRAWAL',
];

export const EXPENSE_PROVIDERS: ExpenseProvider[] = ['ORANGE_MONEY', 'MOOV_MONEY', 'WAVE', 'OTHER'];

export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'SUPPLIES',
  'FUEL',
  'MAINTENANCE',
  'TRANSPORT',
  'UTILITIES',
  'SALARY_ADVANCE',
  'TAXES_FEES',
  'BANK_DEPOSIT',
  'OTHER',
];

export const EXPENSE_STATUSES: ExpenseStatus[] = ['RECORDED', 'CANCELLED'];

/* -------------------------------------------------------------------------- */
/* Règles de saisie par moyen de règlement                                     */
/* -------------------------------------------------------------------------- */

/** L'opérateur n'a de sens que pour le mobile money (`RecordExpenseRequest`). */
export function isProviderRequired(method: ExpenseMethod): boolean {
  return method === 'MOBILE_MONEY';
}

/** Le chèque exige son numéro, porté par `transactionReference`. */
export function isChequeNumberRequired(method: ExpenseMethod): boolean {
  return method === 'CHEQUE';
}

/** Référence facultative : mobile money, virement, retrait en banque. Jamais pour les espèces. */
export function isReferenceOffered(method: ExpenseMethod): boolean {
  return method !== 'CASH';
}

/** Une dépense en espèces exige une caisse ouverte et y crée une sortie (A-058). */
export function needsCashSession(method: ExpenseMethod): boolean {
  return method === 'CASH';
}

/* -------------------------------------------------------------------------- */
/* Teintes                                                                     */
/* -------------------------------------------------------------------------- */

const METHOD_TONES: Record<ExpenseMethod, Tone> = {
  CASH: 'green',
  MOBILE_MONEY: 'amber',
  CHEQUE: 'neutral',
  BANK_TRANSFER: 'blue',
  BANK_WITHDRAWAL: 'violet',
};

const PROVIDER_TONES: Partial<Record<ExpenseProvider, Tone>> = {
  ORANGE_MONEY: 'amber',
  MOOV_MONEY: 'blue',
  WAVE: 'cyan',
};

export function expenseMethodTone(method: ExpenseMethod, provider?: ExpenseProvider | null): Tone {
  const providerTone = provider ? PROVIDER_TONES[provider] : undefined;
  return providerTone ?? METHOD_TONES[method];
}

const STATUS_TONES: Record<ExpenseStatus, Tone> = {
  RECORDED: 'green',
  CANCELLED: 'dark',
};

export function expenseStatusTone(status: ExpenseStatus): Tone {
  return STATUS_TONES[status];
}

/**
 * Actions ouvertes sur une dépense, croisées avec les permissions. Le serveur
 * reste l'autorité : le filtre évite de proposer une action vouée au 403/409.
 */
export function expenseActions(expense: Expense, can: (...codes: string[]) => boolean) {
  return {
    cancel: expense.status === 'RECORDED' && can('expense.cancel'),
    openCashSession: expense.cashSessionId !== null && can('cash.read'),
  };
}

/* -------------------------------------------------------------------------- */
/* Lien caisse → dépense                                                       */
/* -------------------------------------------------------------------------- */

const EXPENSE_REFERENCE = /\bEXP-\d{4}-\d{6}\b/;

/**
 * Référence `EXP-AAAA-NNNNNN` portée par un mouvement de caisse de nature
 * `EXPENSE`. Le contrat `CashMovement` n'expose pas l'identifiant de la
 * dépense : la référence est lue dans le libellé (ou la catégorie) que le
 * serveur y inscrit à la création (A-058).
 */
export function expenseReferenceOf(
  movement: Pick<CashMovement, 'kind' | 'description' | 'category'>,
): string | null {
  if (movement.kind !== 'EXPENSE') return null;
  return (
    EXPENSE_REFERENCE.exec(movement.description)?.[0] ??
    EXPENSE_REFERENCE.exec(movement.category ?? '')?.[0] ??
    null
  );
}
